/**
 * 2D/3D simplex-style value noise for procedural placement and organic motion.
 *
 * We use a permutation-table gradient noise (Perlin-derived) rather than pulling
 * in a dependency. It is seedable through the shared RNG so world layout is
 * reproducible. Only the dimensions we actually need (2D + a light 3D) are
 * implemented to keep the surface small.
 */

import { RNG } from './rng';

const GRAD3 = [
  [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
  [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
  [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
];

export class Noise {
  private perm = new Uint8Array(512);
  private permMod12 = new Uint8Array(512);

  constructor(seedOrRng: number | RNG = 1) {
    const rng = typeof seedOrRng === 'number' ? new RNG(seedOrRng) : seedOrRng;
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) p[i] = i;
    // Seeded shuffle of the permutation table.
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(rng.next() * (i + 1));
      const t = p[i];
      p[i] = p[j];
      p[j] = t;
    }
    for (let i = 0; i < 512; i++) {
      this.perm[i] = p[i & 255];
      this.permMod12[i] = this.perm[i] % 12;
    }
  }

  private static fade(t: number): number {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }
  private static lerp(a: number, b: number, t: number): number {
    return a + t * (b - a);
  }

  /** Classic Perlin 2D noise in roughly [-1, 1]. */
  noise2D(x: number, y: number): number {
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    const u = Noise.fade(x);
    const v = Noise.fade(y);
    const A = this.perm[X] + Y;
    const B = this.perm[X + 1] + Y;
    const grad = (hash: number, xx: number, yy: number) => {
      const g = GRAD3[this.permMod12[hash]];
      return g[0] * xx + g[1] * yy;
    };
    return Noise.lerp(
      Noise.lerp(grad(A, x, y), grad(B, x - 1, y), u),
      Noise.lerp(grad(A + 1, x, y - 1), grad(B + 1, x - 1, y - 1), u),
      v,
    );
  }

  /** Fractal Brownian motion — layered noise for richer structure. */
  fbm2D(x: number, y: number, octaves = 4, lacunarity = 2, gain = 0.5): number {
    let amp = 0.5;
    let freq = 1;
    let sum = 0;
    let norm = 0;
    for (let i = 0; i < octaves; i++) {
      sum += amp * this.noise2D(x * freq, y * freq);
      norm += amp;
      amp *= gain;
      freq *= lacunarity;
    }
    return sum / norm;
  }

  /** Ridged noise — good for road/ridge-like features. */
  ridged2D(x: number, y: number, octaves = 4): number {
    let amp = 0.5;
    let freq = 1;
    let sum = 0;
    for (let i = 0; i < octaves; i++) {
      const n = 1 - Math.abs(this.noise2D(x * freq, y * freq));
      sum += amp * n * n;
      amp *= 0.5;
      freq *= 2;
    }
    return sum;
  }
}
